import clsx from 'clsx';
import _ from 'lodash';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { patientConfig } from '../../../../store/reducers/externalConfig';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import PatientHelper from '../../../../controls/helpers/patientHelper';
import { setSnackbar } from '../../../../store/reducers/globals';
import { ctxAgeUnknown, ctxPatientBannerType, ctxPatientBed } from '../../../../store/reducers/patientContext';
import { AgylePtDetailsIcon } from '../../../../components/icons/agyle';
import IconButton from '@mui/material/IconButton';
import { PATIENT_BANNER_TYPE } from '../../../ward/forms/formsUtil';
import useOpenState from '../../../../hooks/useOpenState';
import PatientDemographicModal from '../../../../components/patient/patientDemographicModal';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import { hasOneOfPermission, Permissions, sendUserAudit } from '../../../../store/reducers/user';
import { AUDIT_TYPES } from '../../../../util/auditUtil';
import { useHistory } from 'react-router-dom';
import { PATIENT_TAGS } from '../../../../util/patientUtil';

const useStyles = makeStyles((theme) => ({
    patientInfoHeader: {
        backgroundColor: theme.palette.mode === 'light' ? '#e2e2e2' : '#404040',
        borderRadius: '10px',
        padding: '0.5rem 1rem',
        paddingRight: 5,
        display: 'flex',
        alignItems: 'center',
        marginLeft: 'auto',
        flexWrap: 'wrap',
        flexBasis: 'content',
        gap: 10,
        '&.left': {
            marginLeft: 'unset',
        },
        '&.center': {
            marginRight: 'auto',
        },
        '&.right': {
            marginLeft: 'auto',
        },
    },
    patientBanner: {
        '&.awaiting_dncpr': {
            backgroundColor: '#cf8304',
        },
    },
    iconButton: {
        backgroundColor: theme.palette.background.dialogBorder,
    },
    iconHover: {
        borderRadius: 6,
        '&:hover': {
            backgroundColor: theme.palette.background.dialogBorder,
        },
    },
    whiteIcon: {
        color: '#fff',
    },
}));

const PatientInfoHeader = ({ patient, fromRegistryPage = false, showName = false, showButtonPatientCotext = false }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();

    // Selectors
    const ptAgeUnknown = useSelector(ctxAgeUnknown);
    const patientBedInfo = useSelector(ctxPatientBed);
    const bannerType = useSelector(ctxPatientBannerType);
    const patientCfg = useSelector(patientConfig);

    const [isDemographicOpen, openDemographic, closeDemographic] = useOpenState(false, true);

    useEffect(() => {}, [patient]);

    const getPatientNSHNumber = (patient) => {
        return patient?.patient ? t(PatientHelper.getPatientNSHNumber(patient.patient)) : '';
    };

    const getPatientFullName = (patient) => {
        return patient?.patient ? PatientHelper.getPatientFullName(patient.patient) : '';
    };

    const getPatientDob = (patient) => {
        if (ptAgeUnknown) return 'Unknown';
        return patient?.patient ? PatientHelper.getPatientDob(patient.patient) : '';
    };

    const getPatientAge = (patient) => {
        if (ptAgeUnknown) return 'Unknown';
        return patient?.patient ? PatientHelper.getPatientAge(patient.patient) : '';
    };

    const getPatientGender = (patient) => {
        return patient?.patient ? PatientHelper.getPatientGender(patient.patient) : '';
    };

    const getPatientHospitalNumber = (patient) => {
        return patient?.patient ? PatientHelper.getPatientHospitalNumber(patient.patient) : '';
    };

    const handleCopyPtNumber = (number) => {
        if (!navigator.clipboard || !number) return;
        navigator.clipboard.writeText(number).then(() => {
            dispatch(
                setSnackbar({
                    type: 'success',
                    message: t('The patient number has been successfully copied to your clipboard.'),
                }),
            );
        });
    };

    const getBackgroundColor = () => {
        let colors = {
            backgroundColor: '',
            color: '',
        };
        switch (bannerType) {
            case PATIENT_BANNER_TYPE.AWAITING_DNCPR:
                colors.backgroundColor = '#B56600';
                break;
            case PATIENT_BANNER_TYPE.ACTIVE_DNCPR:
                colors.backgroundColor = '#CC494F';
                break;
            case PATIENT_BANNER_TYPE.DECEASED:
                colors.backgroundColor = '#000000';
                colors.color = '#FFFFFF';
                break;
            case PATIENT_BANNER_TYPE.UNSET:
            default:
        }
        return colors;
    };

    const handleToggleDemographic = () => {
        if (isDemographicOpen) closeDemographic();
        else openDemographic();
    };

    const patientInfo = [
        {
            key: 'Name',
            value: getPatientFullName(patient),
            render: showName,
        },
        {
            key: 'DOB',
            value: getPatientDob(patient),
            render: true,
        },
        {
            key: 'Age',
            value: getPatientAge(patient),
            render: true,
        },
        {
            key: 'Sex',
            value: getPatientGender(patient),
            render: true,
        },
        {
            key: 'Hosp. No',
            value: getPatientHospitalNumber(patient),
            render: true,
            func: () => handleCopyPtNumber(getPatientHospitalNumber(patient)),
        },
        {
            key: 'NHS No',
            value: getPatientNSHNumber(patient),
            render: true,
            func: () => handleCopyPtNumber(getPatientNSHNumber(patient)),
        },
        {
            key: 'Bed',
            value: patientBedInfo,
            render: patientBedInfo,
        },
    ];

    const handleOpenPatientContext = () => {
        if (!patient) return;
        const isSearchLock = patient?.patient?.tags?.includes(PATIENT_TAGS.SEARCH_LOCKED);

        if (isSearchLock) {
            dispatch(
                setSnackbar({
                    type: 'warning',
                    message: 'The patient record is restricted.',
                }),
            );
        } else if (!dispatch(hasOneOfPermission([Permissions.AGYLE_CAN_OPEN_PATIENT_RECORD, Permissions.AGYLE_CAN_VIEW_ONLY]))) {
            dispatch(
                setSnackbar({
                    type: 'warning',
                    message: 'You do not have permission to view this patient record.',
                }),
            );
        } else {
            dispatch(sendUserAudit(patient, AUDIT_TYPES.OPEN_PATIENT_CONTEXT));
            history.push(`/patient-context/${patient?.patient?.__id}`);
        }
    };

    return (
        <>
            <Grid
                item
                xs={12}
                className={clsx(classes.patientInfoHeader, !fromRegistryPage ? patientCfg?.demographicPosition || '' : '')}
                style={{ ...getBackgroundColor(), justifyContent: fromRegistryPage ? 'center' : '' }}
            >
                {patientInfo.map(
                    ({ key, value, func, render }) =>
                        (value || _.isNumber(value)) &&
                        render && (
                            <div
                                key={key}
                                style={{
                                    display: 'flex',
                                    flexWrap: 'nowrap',
                                    gap: 5,
                                    alignItems: 'center',
                                    cursor: func && 'pointer',
                                    padding: 2,
                                }}
                                className={clsx(func && classes.iconHover)}
                                onClick={func || null}
                            >
                                <Typography variant="body1" style={{ minWidth: 'max-content' }} key={key + value} fontSize="small">
                                    {t(key)}:
                                </Typography>

                                <Typography variant="subtitle1" key={value + key} sx={{ fontWeight: 'bold' }} fontSize="small">
                                    {value}
                                </Typography>
                            </div>
                        ),
                )}

                {!fromRegistryPage && (
                    <IconButton
                        size="small"
                        onClick={handleToggleDemographic}
                        className={clsx(
                            classes.iconHover,
                            isDemographicOpen && classes.iconButton,
                            bannerType === PATIENT_BANNER_TYPE.DECEASED && classes.whiteIcon,
                        )}
                        color={isDemographicOpen ? 'primary' : 'default'}
                    >
                        <AgylePtDetailsIcon fontSize="small" />
                    </IconButton>
                )}

                {showButtonPatientCotext && (
                    <AgyleButton size="small" style={{ marginLeft: 'auto' }} onClick={handleOpenPatientContext}>
                        {t('Patient Context')}
                    </AgyleButton>
                )}
            </Grid>

            {!fromRegistryPage && <PatientDemographicModal patient={patient} open={isDemographicOpen} onClose={closeDemographic} />}
        </>
    );
};

export default PatientInfoHeader;
